<template>
  <transition name="modal-fade">
    <div v-if="visible" class="field-modal-overlay" @click.self="handleOverlayClick">
      <div class="field-modal">
        <!-- Modal Header -->
        <div class="modal-header">
          <h4 class="modal-title">
            <i :class="fieldTypeIcon"></i>
            {{ title }}
          </h4>
          <button class="close-btn" @click="handleClose" aria-label="Close modal">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Field Info -->
        <div class="field-info" v-if="field">
          <span class="field-type-badge" :class="`type-${fieldType}`">
            {{ formatFieldType(fieldType) }}
          </span>
          <span v-if="field.required" class="required-badge">Required</span>
          <span class="assignee-info" v-if="signeeType">
            Assigned to: <strong>{{ formatSigneeType(signeeType) }}</strong>
          </span>
        </div>

        <!-- Field Content -->
        <div class="modal-content">
          <!-- Signature Field -->
          <div v-if="fieldType === 'signature'" class="signature-container" :class="{ 'readonly': readonly }">
            <div class="canvas-container" ref="canvasContainer">
              <VueSignaturePad v-if="!readonly" :width="canvasWidth + 'px'" :height="canvasHeight + 'px'"
                :options="signaturePadOptions" ref="signaturePad" />
              <div v-if="readonly" class="signature-readonly-placeholder">
                <i class="fa fa-lock"></i>
                <p>Signature field (view only)</p>
              </div>
              <div v-else-if="!hasSignature" class="signature-hint">
                <i class="fa fa-hand-pointer"></i>
                <p>Draw your signature</p>
              </div>
            </div>
          </div>

          <!-- Text Field -->
          <div v-else-if="fieldType === 'text'" class="text-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <input :id="fieldId" type="text" v-model="fieldValue" class="form-control" :placeholder="placeholder"
              :required="field && field.required" :disabled="readonly" />
          </div>

          <!-- Textarea Field -->
          <div v-else-if="fieldType === 'textarea'" class="textarea-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <textarea :id="fieldId" v-model="fieldValue" class="form-control" :placeholder="placeholder"
              :required="field && field.required" :disabled="readonly" rows="4"></textarea>
          </div>

          <!-- Email Field -->
          <div v-else-if="fieldType === 'email'" class="email-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <input :id="fieldId" type="email" v-model="fieldValue" class="form-control"
              :placeholder="placeholder || 'email@example.com'" :required="field && field.required"
              :disabled="readonly" />
          </div>

          <!-- Phone Field -->
          <div v-else-if="fieldType === 'phone'" class="phone-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <input :id="fieldId" type="tel" v-model="fieldValue" class="form-control"
              :placeholder="placeholder || '(555) 555-5555'" :required="field && field.required" :disabled="readonly" />
          </div>

          <!-- Number Field -->
          <div v-else-if="fieldType === 'number'" class="number-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <input :id="fieldId" type="number" v-model="fieldValue" class="form-control" :placeholder="placeholder"
              :required="field && field.required" :disabled="readonly" />
          </div>

          <!-- Date Field -->
          <div v-else-if="fieldType === 'date'" class="date-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <input :id="fieldId" type="date" v-model="fieldValue" class="form-control"
              :required="field && field.required" :disabled="readonly" />
          </div>

          <!-- Select/Dropdown Field -->
          <div v-else-if="fieldType === 'select' || fieldType === 'dropdown'" class="select-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <select :id="fieldId" v-model="fieldValue" class="form-control" :required="field && field.required"
              :disabled="readonly">
              <option value="">-- Select an option --</option>
              <option v-for="option in fieldOptions" :key="option.value || option" :value="option.value || option">
                {{ option.label || option }}
              </option>
            </select>
          </div>

          <!-- Checkbox Field -->
          <div v-else-if="fieldType === 'checkbox'" class="checkbox-field-container">
            <label class="checkbox-label">
              <input type="checkbox" v-model="fieldValue" :required="field && field.required" :disabled="readonly" />
              <span>{{ fieldLabel }}</span>
            </label>
          </div>

          <!-- Radio Field -->
          <div v-else-if="fieldType === 'radio'" class="radio-field-container">
            <label>{{ fieldLabel }}</label>
            <div class="radio-options">
              <label v-for="option in fieldOptions" :key="option.value || option" class="radio-label">
                <input type="radio" v-model="fieldValue" :value="option.value || option"
                  :required="field && field.required" :disabled="readonly" />
                <span>{{ option.label || option }}</span>
              </label>
            </div>
          </div>

          <!-- Fallback for unknown types -->
          <div v-else class="text-field-container">
            <label :for="fieldId">{{ fieldLabel }}</label>
            <input :id="fieldId" type="text" v-model="fieldValue" class="form-control" :placeholder="placeholder"
              :required="field && field.required" :disabled="readonly" />
          </div>

          <!-- Readonly Notice -->
          <div v-if="readonly && field && field.read_only" class="readonly-notice">
            <i class="fa fa-lock"></i>
            <span>This field is filled in automatically and cannot be edited.</span>
          </div>
          <div v-else-if="readonly" class="readonly-notice">
            <i class="fa fa-info-circle"></i>
            <span>This field is assigned to another role and cannot be edited by you.</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="handleClose">
            Cancel
          </button>

          <button v-if="fieldType === 'signature'" class="btn btn-default" @click="clearSignature"
            :disabled="!hasSignature">
            <i class="fa fa-redo"></i>
            Clear
          </button>

          <button class="btn btn-primary" @click="saveField" :disabled="saving || !canSave">
            <i class="fa fa-check" v-if="!saving"></i>
            <i class="fa fa-spinner fa-spin" v-else></i>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'FieldModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    field: {
      type: Object,
      default: null
    },
    title: {
      type: String,
      default: 'Edit Field'
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fieldValue: '',
      hasSignature: false,
      saving: false,
      canvasWidth: 500,
      canvasHeight: 200,
      signaturePadOptions: {
        dotSize: 2,
        minWidth: 1.5,
        maxWidth: 3.5,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        penColor: 'rgba(0, 0, 0, 1)',
        velocityFilterWeight: 0.7,
        onBegin: () => {
          this.hasSignature = true;
        }
      }
    };
  },
  computed: {
    fieldType() {
      if (!this.field) return 'text';
      const annotation = this.field.annotations?.[0];
      return annotation?.type || this.field.field_type || 'text';
    },

    fieldId() {
      return this.field?.id || 'field-input';
    },

    fieldLabel() {
      return this.field?.field_label || this.field?.label || this.field?.field_name || this.field?.name || 'Field';
    },

    placeholder() {
      return this.field?.placeholder || `Enter ${this.fieldLabel.toLowerCase()}`;
    },

    signeeType() {
      const annotation = this.field?.annotations?.[0];
      return annotation?.signee_type || this.field?.assigned_to?.role;
    },

    fieldOptions() {
      return this.field?.options || this.field?.field_options || [];
    },

    fieldTypeIcon() {
      const icons = {
        signature: 'fa fa-pen-fancy',
        text: 'fa fa-font',
        textarea: 'fa fa-align-left',
        email: 'fa fa-envelope',
        phone: 'fa fa-phone',
        number: 'fa fa-hashtag',
        date: 'fa fa-calendar',
        select: 'fa fa-list',
        dropdown: 'fa fa-caret-down',
        checkbox: 'fa fa-check-square',
        radio: 'fa fa-dot-circle'
      };
      return icons[this.fieldType] || 'fa fa-edit';
    },

    canSave() {
      if (this.readonly) return false;
      if (this.fieldType === 'signature') {
        return this.hasSignature;
      }
      if (this.field?.required) {
        return this.fieldValue !== '' && this.fieldValue !== null && this.fieldValue !== undefined;
      }
      return true;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.onModalOpen();
      }
    },
    field: {
      immediate: true,
      handler(newField) {
        if (newField) {
          // Initialize with existing value
          this.fieldValue = newField.value || '';
        }
      }
    }
  },
  methods: {
    onModalOpen() {
      // Reset state
      this.saving = false;
      this.hasSignature = false;

      // Initialize value from field
      if (this.field) {
        this.fieldValue = this.field.value || '';
      }

      // Clear signature pad if it exists
      this.$nextTick(() => {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.clearSignature();
        }
      });
    },

    handleOverlayClick() {
      this.handleClose();
    },

    handleClose() {
      this.$emit('close');
    },

    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
        this.hasSignature = false;
      }
    },

    getSignatureData() {
      if (!this.$refs.signaturePad) return null;
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      return isEmpty ? null : data;
    },

    async saveField() {
      if (this.saving || !this.canSave) return;

      this.saving = true;

      try {
        let value;
        if (this.fieldType === 'signature') {
          value = this.getSignatureData();
          if (!value) {
            alert('Please draw your signature first.');
            this.saving = false;
            return;
          }
        } else {
          value = this.fieldValue;
        }

        this.$emit('save', {
          field: this.field,
          value: value,
          fieldType: this.fieldType
        });

        // Close modal after short delay
        setTimeout(() => {
          this.saving = false;
          this.$emit('close');
        }, 300);
      } catch (error) {
        console.error('Error saving field:', error);
        this.saving = false;
        alert('Failed to save. Please try again.');
      }
    },

    formatFieldType(type) {
      const labels = {
        signature: 'Signature',
        text: 'Text',
        textarea: 'Text Area',
        date: 'Date',
        email: 'Email',
        phone: 'Phone',
        number: 'Number',
        select: 'Select',
        dropdown: 'Dropdown',
        checkbox: 'Checkbox',
        radio: 'Radio'
      };
      return labels[type] || type;
    },

    formatSigneeType(type) {
      if (!type) return 'Unassigned';
      const labels = {
        student: 'Student',
        guardian: 'Guardian',
        instructor: 'Instructor',
        staff: 'Staff',
        admin: 'Admin'
      };
      return labels[type] || type;
    }
  }
};
</script>

<style scoped>
/* Overlay */
.field-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Modal Container */
.field-modal {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

/*
 * Bootstrap 3's .modal-header applies @include clearfix, which adds
 * ::before/::after pseudo-elements with display: table. Inside our flex
 * container those become phantom flex items, pushing the title and close
 * button toward the middle. Disable them here.
 */
.modal-header::before,
.modal-header::after {
  display: none;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-title i {
  color: #4299e1;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #718096;
  cursor: pointer;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background-color: #f7fafc;
  color: #2d3748;
}

/* Field Info */
.field-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background-color: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.field-type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.type-signature {
  background-color: #fef3c7;
  color: #92400e;
}

.type-text,
.type-textarea {
  background-color: #e0e7ff;
  color: #3730a3;
}

.type-date {
  background-color: #fce7f3;
  color: #9d174d;
}

.type-email {
  background-color: #cffafe;
  color: #0e7490;
}

.type-phone {
  background-color: #d1fae5;
  color: #065f46;
}

.type-number {
  background-color: #fef9c3;
  color: #854d0e;
}

.type-select,
.type-dropdown {
  background-color: #ede9fe;
  color: #5b21b6;
}

.type-checkbox,
.type-radio {
  background-color: #f3e8ff;
  color: #7c3aed;
}

.required-badge {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.assignee-info {
  font-size: 13px;
  color: #4a5568;
  margin-left: auto;
}

/* Modal Content */
.modal-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  box-shadow: none !important;
  border: none;
}

/* Form Controls */
.modal-content label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
}

.modal-content .form-control {
  width: 100%;
  /*
     * Bootstrap 3's .form-control hardcodes `height: $input-height-base`
     * (~34px) sized for Bootstrap's default 14px font + 6px padding. Our
     * modal uses a 16px font with 12px padding, so that fixed height
     * leaves a content box far too small for the text (descenders on
     * `y` in `yyyy` get clipped inside `<input type="date">`). Let the
     * input size itself to its content.
     */
  height: auto;
  padding: 12px 16px;
  font-size: 16px;
  line-height: 1.5;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  transition: border-color 0.2s;
  /*
     * Override the legacy global `input { text-align: center }` rule in
     * src/assets/css/partials/inputs.scss. Without this the date input's
     * mm/dd/yyyy format text (and text/email/phone placeholders) renders
     * centered.
     */
  text-align: left;
}

.modal-content .form-control:focus {
  outline: none;
  border-color: #4299e1;
}

.modal-content textarea.form-control {
  resize: vertical;
  min-height: 120px;
}

.modal-content select.form-control {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 44px;
}

/* Checkbox & Radio */
.checkbox-label,
.radio-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 400;
}

.checkbox-label input,
.radio-label input {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.radio-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

/* Signature Container */
.signature-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.canvas-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f7fafc;
  border-radius: 8px;
  padding: 16px;
}

.canvas-container>>>canvas {
  border: 2px solid #cbd5e0;
  border-radius: 8px;
  background-color: #ffffff;
  cursor: crosshair;
  touch-action: none;
}

.signature-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #a0aec0;
  pointer-events: none;
  user-select: none;
}

.signature-hint i {
  font-size: 36px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.signature-hint p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

/* Readonly Signature Placeholder */
.signature-readonly-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 150px;
  background-color: #f1f5f9;
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  color: #64748b;
}

.signature-readonly-placeholder i {
  font-size: 32px;
  margin-bottom: 8px;
  opacity: 0.6;
}

.signature-readonly-placeholder p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

/* Readonly Notice */
.readonly-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 16px;
  background-color: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  font-size: 13px;
  color: #92400e;
}

.readonly-notice i {
  font-size: 16px;
  color: #f59e0b;
  flex-shrink: 0;
}

/* Disabled Form Controls */
.modal-content .form-control:disabled {
  background-color: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.8;
}

.checkbox-label input:disabled,
.radio-label input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.checkbox-label input:disabled+span,
.radio-label input:disabled+span {
  color: #64748b;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  background-color: #ffffff;
  flex-shrink: 0;
}

.modal-actions .btn {
  min-height: 44px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
}

.modal-actions .btn-secondary {
  background-color: #f7fafc;
  border: 1px solid #cbd5e0;
  color: #4a5568;
}

.modal-actions .btn-secondary:hover {
  background-color: #edf2f7;
}

.modal-actions .btn-default {
  background-color: #ffffff;
  border: 1px solid #cbd5e0;
  color: #4a5568;
}

.modal-actions .btn-default:hover:not(:disabled) {
  background-color: #f7fafc;
}

.modal-actions .btn-default:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-actions .btn-primary {
  background-color: #4299e1;
  border: 1px solid #4299e1;
  color: #ffffff;
  flex: 1;
}

.modal-actions .btn-primary:hover:not(:disabled) {
  background-color: #3182ce;
}

.modal-actions .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Transition Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .field-modal,
.modal-fade-leave-active .field-modal {
  transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .field-modal,
.modal-fade-leave-to .field-modal {
  transform: scale(0.95) translateY(20px);
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .field-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .field-modal {
    border-radius: 16px 16px 0 0;
    max-height: 85vh;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-title {
    font-size: 18px;
  }

  .field-info {
    padding: 10px 20px;
  }

  .modal-content {
    padding: 20px;
  }

  .modal-actions {
    padding: 16px 20px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0));
  }

  .modal-actions .btn {
    font-size: 14px;
  }
}
</style>
